import {
  InterventionReport,
  InterventionCompletionStatus,
  UserType,
} from '@inplay/models';

export type SectionPermission = {
  editable: boolean;
};

export type InterventionReportEditPermissions = {
  canEdit: boolean;
  sections: {
    topSection: SectionPermission;
    notesAndObservations: SectionPermission;
    reviewSection: SectionPermission;
  };
};

const LOCKED: InterventionReportEditPermissions = {
  canEdit: false,
  sections: {
    topSection: { editable: false },
    notesAndObservations: { editable: false },
    reviewSection: { editable: false },
  },
};

const FULLY_EDITABLE: InterventionReportEditPermissions = {
  canEdit: true,
  sections: {
    topSection: { editable: true },
    notesAndObservations: { editable: true },
    reviewSection: { editable: true },
  },
};

// Auto-generated (missed): admins can edit notes only
const NOTES_ONLY: InterventionReportEditPermissions = {
  canEdit: true,
  sections: {
    topSection: { editable: false },
    notesAndObservations: { editable: true },
    reviewSection: { editable: false },
  },
};

// Review section only (after review complete, before weekly review)
const REVIEW_SECTION_ONLY: InterventionReportEditPermissions = {
  canEdit: true,
  sections: {
    topSection: { editable: false },
    notesAndObservations: { editable: false },
    reviewSection: { editable: true },
  },
};

const isStaffRole = (userType: UserType | undefined): boolean =>
  userType === UserType.VENUE_STAFF;

const isMissedIntervention = (report: InterventionReport): boolean =>
  report.interventionStatus === InterventionCompletionStatus.MISSED;

const isReviewComplete = (report: InterventionReport): boolean =>
  !!report.reviewedBy;

const isReportCreator = (
  report: InterventionReport,
  currentUserId?: string,
): boolean => !!currentUserId && report.createdBy?.userId === currentUserId;

/**
 * Computes edit permissions for an intervention report.
 *
 * Rules:
 * - Scenario 01 (Report created, review pending):
 *   - Manual (complete): staff creator can edit, other staff locked, admins full edit
 *   - Auto (missed): staff locked, admins notes only
 * - Scenario 02 (Review complete, before weekly review):
 *   - All staff locked
 *   - All admins: review section only
 * - After weekly review: fully locked (TODO: check weekly review status when available)
 */
export const getInterventionReportEditPermissions = (
  report: InterventionReport,
  userType: UserType | undefined,
  currentUserId?: string,
): InterventionReportEditPermissions => {
  const staff = isStaffRole(userType);
  const missed = isMissedIntervention(report);
  const reviewed = isReviewComplete(report);

  // TODO: When weekly review feature is added, check if weekly report exists
  // If weekly review complete → return LOCKED for everyone
  // const weeklyReviewDone = report.weeklyReviewId != null;
  // if (weeklyReviewDone) return LOCKED;

  // ═══════════════════════════════════════════════
  // SCENARIO 02: Review complete (before weekly review)
  // ═══════════════════════════════════════════════
  if (reviewed) {
    // Staff: always locked after review
    if (staff) return LOCKED;
    // Admins: review section only
    return REVIEW_SECTION_ONLY;
  }

  // ═══════════════════════════════════════════════
  // SCENARIO 01: Report created, review pending
  // ═══════════════════════════════════════════════

  // Auto-generated (Intervention Missed)
  if (missed) {
    if (staff) return LOCKED;
    return NOTES_ONLY;
  }

  // Manual (Intervention Complete)
  if (staff) {
    return isReportCreator(report, currentUserId) ? FULLY_EDITABLE : LOCKED;
  }
  return FULLY_EDITABLE;
};
